// PatientImageGrid.tsx
/**
 * Lazy-loading grid for patient images with skeleton placeholders.
 * - Shows skeleton tiles until the first image is confirmed loaded.
 * - Builds full-res URL array only when the modal is open.
 * - Removes images that fail both thumbnail and full URLs (no "Failed to load" tile).
 * - Measures container width to size the grid correctly on mobile.
 */
import React, { useState, useCallback, useMemo, useContext } from "react";
import {
  View,
  Image,
  StyleSheet,
  FlatList,
  Pressable,
  useWindowDimensions,
  Text,
  ActivityIndicator,
  LayoutChangeEvent,
} from "react-native";
import { TGetPatientImageData } from "../../../../services/api";
import {
  createImageEdit,
  getImageEditJob,
} from "../../../../services/api-clinic";
import GalleryModal, {
  GalleryEditableImageTarget,
} from "../../../common/native/GalleryModal";
import { buildPhotoDeepLinkUrl } from "../../../../utils/photoShare";
import { createUUID } from "../../../../utils/functions";
import { SessionContext } from "../../../../contexts";

interface PatientImageGridProps {
  images: TGetPatientImageData[];
  isVisible?: boolean; // Only load when section is expanded
  initialCount?: number; // render only first N thumbs initially (optional)
  onEditComplete?: () => void | Promise<void>;
}

interface ImageLoadState {
  [imageId: string]: {
    thumbnailLoaded: boolean;
    thumbnailFailed: boolean;
    fullImageLoaded: boolean;
    fullImageFailed: boolean;
  };
}

export const PatientImageGrid: React.FC<PatientImageGridProps> = ({
  images,
  isVisible = true,
  initialCount = 24,
  onEditComplete,
}) => {
  const session = useContext(SessionContext)?.session;
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageLoadStates, setImageLoadStates] = useState<ImageLoadState>({});
  const [anyLoaded, setAnyLoaded] = useState(false);
  const { width: screenWidth } = useWindowDimensions();
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  // Measure container; fall back to (screen - padding) for first render
  const fallbackWidth = Math.max(0, screenWidth - 32); // ~16px padding each side
  const widthForCalc = containerWidth ?? fallbackWidth;

  // Responsive grid sizing based on measured container width
  const TARGET_TILE = 120; // desired tile width
  const TILE_GAP = 4; // horizontal spacing from margins (2 + 2)
  const numColumns = Math.max(
    2,
    Math.floor(widthForCalc / (TARGET_TILE + TILE_GAP)),
  );
  const imageSize = Math.floor(
    (widthForCalc - (numColumns - 1) * TILE_GAP) / numColumns,
  );

  const handleImageLoadError = useCallback(
    (imageId: string, isFullImage: boolean) => {
      setImageLoadStates((prev) => ({
        ...prev,
        [imageId]: {
          ...prev[imageId],
          [isFullImage ? "fullImageFailed" : "thumbnailFailed"]: true,
        },
      }));
    },
    [],
  );

  const handleImageLoadSuccess = useCallback(
    (imageId: string, isFullImage: boolean) => {
      setImageLoadStates((prev) => {
        const next = {
          ...prev,
          [imageId]: {
            ...prev[imageId],
            [isFullImage ? "fullImageLoaded" : "thumbnailLoaded"]: true,
          },
        };
        if (!anyLoaded) setAnyLoaded(true);
        return next;
      });
    },
    [anyLoaded],
  );

  // Image considered "dead" when both thumb and full have failed (or are missing)
  const isImageDead = (
    img: TGetPatientImageData,
    loadState?: { thumbnailFailed?: boolean; fullImageFailed?: boolean },
  ) => {
    const hasThumb = !!img.thumbnail_presigned_urls?.[0];
    const hasFull = !!img.presigned_urls?.[0];
    const thumbDead = loadState?.thumbnailFailed || !hasThumb;
    const fullDead = loadState?.fullImageFailed || !hasFull;
    return thumbDead && fullDead;
  };

  const getThumbnailUrl = useCallback(
    (image: TGetPatientImageData): string | null => {
      const imageId = String(image.image_data?.id || "");
      const loadState = imageLoadStates[imageId];

      const thumbnailUrl = image.thumbnail_presigned_urls?.[0];
      if (thumbnailUrl && !loadState?.thumbnailFailed) return thumbnailUrl;

      const fullUrl = image.presigned_urls?.[0];
      if (fullUrl && !loadState?.fullImageFailed) return fullUrl;

      return null;
    },
    [imageLoadStates],
  );

  const getFullImageUrl = useCallback((image: TGetPatientImageData): string => {
    return (
      image.presigned_urls?.[0] || image.thumbnail_presigned_urls?.[0] || ""
    );
  }, []);

  // 1) Start with images that have at least one candidate URL.
  // 2) Filter out images that are fully dead based on current load state.
  const filteredImages = useMemo(() => {
    if (!isVisible) return [];
    const base = images.filter((img) => {
      const t = img.thumbnail_presigned_urls?.[0];
      const f = img.presigned_urls?.[0];
      return t || f;
    });
    return base.filter((img) => {
      const imageId = String(img.image_data?.id || "");
      return !isImageDead(img, imageLoadStates[imageId]);
    });
  }, [images, isVisible, imageLoadStates]);

  // Apply initialCount cap (optional)
  const renderImages = useMemo(() => {
    return filteredImages.slice(0, initialCount);
  }, [filteredImages, initialCount]);

  // Only build full-res URL list when the modal is open
  const fullImageUrls = useMemo(() => {
    if (!modalVisible) return [];
    return renderImages.map(getFullImageUrl);
  }, [modalVisible, renderImages, getFullImageUrl]);

  const imageIds = useMemo(
    () => renderImages.map((item) => item.image_data?.id),
    [renderImages],
  );

  const editableImages = useMemo<Array<GalleryEditableImageTarget | null>>(
    () =>
      renderImages.map((item) => {
        if (item.edit_target) {
          return {
            targetType: item.edit_target.target_type,
            targetId: item.edit_target.target_id,
          };
        }
        const imageId = item.image_data?.id;
        if (!imageId || item.image_data?.upload_type === "before_after") {
          return null;
        }
        return {
          targetType: "clinic_patient_image",
          targetId: imageId,
        };
      }),
    [renderImages],
  );

  const getShareLinkForIndex = useCallback(
    (index: number): string | null => {
      const imageId = imageIds[index];
      if (imageId === null || imageId === undefined) return null;
      return buildPhotoDeepLinkUrl(imageId);
    },
    [imageIds],
  );

  const openModal = useCallback((index: number) => {
    setSelectedIndex(index);
    setModalVisible(true);
  }, []);

  const handleSaveEdit = useCallback(
    async ({
      target,
      degrees,
    }: {
      target: GalleryEditableImageTarget;
      degrees: number;
    }) => {
      if (!session) {
        throw new Error("Session unavailable. Sign in again and retry.");
      }

      const job = await createImageEdit(session, {
        target_type: target.targetType,
        target_id: target.targetId,
        operations: [{ type: "rotate", degrees }],
        client_request_id: createUUID(),
      });

      const startedAt = Date.now();
      const timeoutMs = 60000;
      const pollDelayMs = 1500;

      while (Date.now() - startedAt < timeoutMs) {
        const current = await getImageEditJob(session, job.task_id);
        if (current.status === "completed") {
          await onEditComplete?.();
          return;
        }
        if (current.status === "failed") {
          throw new Error(
            current.error_message || "Unable to save changes. Try again.",
          );
        }
        await new Promise((resolve) => setTimeout(resolve, pollDelayMs));
      }

      throw new Error("Image edit is still processing. Try again shortly.");
    },
    [session, onEditComplete],
  );

  if (!isVisible) return null;

  if (renderImages.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No images available</Text>
      </View>
    );
  }

  return (
    <View
      style={styles.container}
      onLayout={(e: LayoutChangeEvent) =>
        setContainerWidth(e.nativeEvent.layout.width)
      }
    >
      {/* Show skeletons until at least one image loads */}
      {!anyLoaded && (
        <View style={styles.skeletonContainer}>
          {Array.from({
            length: Math.min(renderImages.length, Math.max(numColumns * 2, 6)),
          }).map((_, i) => (
            <View
              key={`skeleton-${i}`}
              style={[
                styles.skeletonTile,
                { width: imageSize, height: imageSize },
              ]}
            >
              <ActivityIndicator />
            </View>
          ))}
        </View>
      )}

      <FlatList
        key={`grid-${numColumns}`}
        data={renderImages}
        keyExtractor={(item, index) => `${item.image_data?.id || index}`}
        numColumns={numColumns}
        scrollEnabled={false}
        contentContainerStyle={styles.gridContainer}
        renderItem={({ item, index }) => {
          const imageId = String(item.image_data?.id || "");
          const displayUrl = getThumbnailUrl(item);

          // If no usable URL at this moment, render nothing (item will be filtered next render)
          if (!displayUrl) return null;

          const isShowingThumbnail =
            displayUrl === item.thumbnail_presigned_urls?.[0];

          return (
            <Pressable
              onPress={() => openModal(index)}
              style={[
                styles.imageWrapper,
                { width: imageSize, height: imageSize },
              ]}
            >
              <Image
                source={{ uri: displayUrl }}
                style={styles.image}
                resizeMode="cover"
                onError={() =>
                  handleImageLoadError(imageId, !isShowingThumbnail)
                }
                onLoad={() =>
                  handleImageLoadSuccess(imageId, !isShowingThumbnail)
                }
                onLoadStart={() => {
                  if (!imageLoadStates[imageId]) {
                    setImageLoadStates((prev) => ({
                      ...prev,
                      [imageId]: {
                        thumbnailLoaded: false,
                        thumbnailFailed: false,
                        fullImageLoaded: false,
                        fullImageFailed: false,
                      },
                    }));
                  }
                }}
              />
            </Pressable>
          );
        }}
      />

      <GalleryModal
        visible={modalVisible}
        imageUrls={fullImageUrls}
        selectedIndex={selectedIndex}
        onClose={() => setModalVisible(false)}
        shareLinkForIndex={getShareLinkForIndex}
        editableImages={editableImages}
        onSaveEdit={handleSaveEdit}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  gridContainer: {
    // gap: 4, // optional; not reliable on all RN versions. Using margins on tiles instead.
    paddingBottom: 2,
  },
  imageWrapper: {
    margin: 2,
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
  },
  image: { width: "100%", height: "100%" },

  // skeletons
  skeletonContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 4,
  },
  skeletonTile: {
    margin: 2,
    borderRadius: 6,
    backgroundColor: "#eee",
    alignItems: "center",
    justifyContent: "center",
  },

  // kept in case you repurpose styles later
  errorWrapper: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#ffebee",
  },
  errorText: { fontSize: 10, color: "#d32f2f", textAlign: "center" },
  emptyContainer: { padding: 20, alignItems: "center" },
  emptyText: { color: "#666", fontSize: 14 },
});
